import type { RealtimeVoice } from "../../shared/realtime";
import type { ImageProvider } from "../../shared/imageProvider";

export type ClientToServerMessage =
  | {
      type: "configure";
      authToken?: string;
      voice?: RealtimeVoice;
      imageProvider?: ImageProvider;
    }
  | { type: "audio.append"; audio: string; videoFrames?: string[]; maxSliceNums?: number }
  | { type: "audio.commit" }
  | { type: "audio.interrupt" }
  | { type: "text.send"; text: string }
  | { type: "image.generate"; prompt: string }
  | { type: "director.skip" }
  | { type: "reset" };

export type TurnStageName = "heard" | "framing" | "speaking" | "visualizing" | "ready";
export type MiniCpmRealtimeMode = "audio" | "video";

export type ServerToClientMessage =
  | {
      type: "status";
      status: string;
      detail?: string;
      realtimeMode?: "duplex" | "stateless";
      minicpmMode?: MiniCpmRealtimeMode;
    }
  | { type: "error"; message: string; detail?: string }
  | { type: "choice.prompt"; text: string }
  | { type: "input.locked"; reason: string }
  | { type: "input.ready"; prompt?: string }
  | { type: "turn.stage"; stage: TurnStageName; label: string; text: string; sceneTitle?: string }
  | { type: "narration.show"; title: string; text: string; durationMs?: number; presentation?: "caption" | "ambient" }
  | { type: "scene.transition"; title: string; detail?: string }
  | { type: "scene.camera"; label: string; detail: string; focus: "wide" | "medium" | "detail"; mood: "observe" | "tense" | "reveal" }
  | { type: "director.feedback"; label: string; text: string; mood: "heard" | "observing" | "handoff" }
  | { type: "director.speaking"; reason: string }
  | { type: "director.skipped" }
  | { type: "response.started"; generation: number }
  | { type: "transcript.delta"; role: "assistant" | "user"; text: string; generation?: number }
  | { type: "transcript.done"; role: "assistant" | "user"; text: string; generation?: number }
  | { type: "audio.delta"; audio: string; generation: number }
  | { type: "image.queued"; prompt: string; sceneTitle?: string; replaced?: boolean }
  | { type: "image.pending"; prompt: string; sceneTitle?: string }
  | { type: "image.error"; prompt?: string; message: string; detail?: string }
  | { type: "image.ready"; prompt: string; imageUrl: string; provider?: string; sceneTitle?: string }
  | { type: "story.progress"; beatIndex: number; turnsInBeat: number; turnsRequired: number }
  | { type: "story.reward"; beatIndex: number; title: string; videoUrl: string }
  | { type: "world.state"; state: Record<string, unknown> };

export interface PublicConfig {
  realtimeModel: string;
  realtimeVoice: RealtimeVoice;
  realtimeVoices: RealtimeVoice[];
  imageProvider?: ImageProvider;
  minicpmRealtimeMode?: MiniCpmRealtimeMode;
  defaultInitialImageUrl: string;
}

export async function getPublicConfig() {
  const response = await fetch("/api/config");
  if (!response.ok) throw new Error("无法读取公开配置");
  return (await response.json()) as PublicConfig;
}

export async function createSession() {
  const response = await fetch("/api/sessions", { method: "POST" });
  if (!response.ok) throw new Error("无法创建会话");
  return (await response.json()) as { sessionId: string };
}

export async function login(username: string, password: string) {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password })
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "登录失败" }));
    throw new Error(payload.error ?? "登录失败");
  }

  return (await response.json()) as { token: string; expiresAt: number };
}

export class SessionSocket {
  private socket?: WebSocket;

  constructor(
    private readonly sessionId: string,
    private readonly onMessage: (message: ServerToClientMessage) => void,
    private readonly onStateChange: (state: string) => void
  ) {}

  connect() {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    this.socket = new WebSocket(`${protocol}://${window.location.host}/api/sessions/${this.sessionId}/ws`);

    this.socket.addEventListener("open", () => this.onStateChange("connected"));
    this.socket.addEventListener("close", () => this.onStateChange("closed"));
    this.socket.addEventListener("error", () => this.onStateChange("error"));
    this.socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      this.onMessage(JSON.parse(event.data) as ServerToClientMessage);
    });
  }

  send(message: ClientToServerMessage) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error("会话尚未连接");
    }
    this.socket.send(JSON.stringify(message));
  }

  close() {
    this.socket?.close();
  }
}
